<template>
  <div class="caption-prep-page">
    <section class="caption-hero">
      <div>
        <div class="caption-kicker">{{ pageKicker }}</div>
        <h1>{{ job?.name || job?.pageName || "Prepare Captions" }}</h1>
        <p>
          Add captions now or come back later from the job detail page. Empty
          captions will use {{ fallbackLabel }}.
        </p>
      </div>
      <div class="hero-actions">
        <b-button variant="light" @click="goToJobDetail">
          <b-icon icon="arrow-left"></b-icon>
          Back to Job
        </b-button>
        <b-button variant="outline-primary" @click="saveChangedCaptions" :disabled="!hasChanges || saving">
          <b-spinner v-if="saving" small class="mr-2"></b-spinner>
          Save Changes
        </b-button>
        <b-button
          v-if="canSchedulePending"
          variant="primary"
          @click="startScheduling"
          :disabled="starting || hasChanges"
        >
          <b-spinner v-if="starting" small class="mr-2"></b-spinner>
          {{ scheduleButtonText }}
        </b-button>
      </div>
    </section>

    <section class="caption-toolbar">
      <b-form-input
        v-model="search"
        placeholder="Search filename"
        @keyup.enter="fetchCaptionPosts"
      />
      <b-form-select v-model="captionFilter" :options="filterOptions" />
      <b-button variant="light" @click="fetchCaptionPosts">
        <b-icon icon="search"></b-icon>
      </b-button>
    </section>

    <section class="caption-stats">
      <div>
        <span>Pending posts</span>
        <strong>{{ totalPosts }}</strong>
      </div>
      <div>
        <span>Missing captions</span>
        <strong>{{ missingCaptionCount }}</strong>
      </div>
      <div>
        <span>Saved captions</span>
        <strong>{{ filledCaptionCount }}</strong>
      </div>
      <div>
        <span>Unsaved changes</span>
        <strong>{{ Object.keys(changedCaptions).length }}</strong>
      </div>
      <div>
        <span>Unscheduled pending</span>
        <strong>{{ unscheduledPendingCount }}</strong>
      </div>
    </section>

    <section class="caption-table-card">
      <b-table
        :items="posts"
        :fields="fields"
        :busy="loading"
        responsive
        hover
        class="caption-table"
      >
        <template #cell(thumbnail)="data">
          <button
            v-if="data.item.thumbnailUrl"
            type="button"
            class="caption-thumbnail-button"
            @click="openImagePreview(data.item)"
          >
            <img
              :src="data.item.thumbnailUrl"
              :alt="data.item.fileName || 'Photo thumbnail'"
              class="caption-thumbnail"
            />
            <span class="caption-thumbnail-overlay">
              <b-icon icon="arrows-fullscreen"></b-icon>
            </span>
          </button>
          <div v-else class="caption-thumbnail-placeholder">
            No preview
          </div>
        </template>
        <template #cell(fileName)="data">
          <div class="file-name-cell">
            <span>{{ data.item.fileName }}</span>
          </div>
        </template>
        <template #cell(caption)="data">
          <b-form-textarea
            :value="getCaptionValue(data.item)"
            rows="2"
            max-rows="5"
            placeholder="Write caption..."
            @input="markCaptionChanged(data.item, $event)"
          />
        </template>
        <template #cell(status)="data">
          <span
            class="caption-status"
            :class="getCaptionValue(data.item).trim() ? 'is-filled' : 'is-missing'"
          >
            {{ getCaptionValue(data.item).trim() ? "Captioned" : "Missing" }}
          </span>
        </template>
      </b-table>

      <b-pagination
        v-model="currentPage"
        :total-rows="totalPosts"
        :per-page="pageSize"
        align="center"
        @input="fetchCaptionPosts"
      />
    </section>

    <b-modal
      v-model="showImagePreview"
      id="caption-photo-preview-modal"
      hide-footer
      centered
      size="xl"
      body-class="caption-preview-body"
      header-class="caption-preview-header"
    >
      <template #modal-title>
        <span class="caption-preview-title">
          {{ selectedImagePreview?.fileName || "Photo preview" }}
        </span>
      </template>
      <img
        v-if="selectedImagePreview?.url"
        :src="selectedImagePreview.url"
        :alt="selectedImagePreview.fileName || 'Photo preview'"
        class="caption-preview-image"
      />
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      job: null,
      posts: [],
      loading: false,
      saving: false,
      starting: false,
      changedCaptions: {},
      search: "",
      captionFilter: "all",
      currentPage: 1,
      pageSize: 50,
      totalPosts: 0,
      missingCaptionCount: 0,
      filledCaptionCount: 0,
      unscheduledPendingCount: 0,
      showImagePreview: false,
      selectedImagePreview: null,
      fields: [
        { key: "thumbnail", label: "" },
        { key: "fileName", label: "File Name" },
        { key: "caption", label: "Caption" },
        { key: "status", label: "Status" },
      ],
      filterOptions: [
        { value: "all", text: "All captions" },
        { value: "missing", text: "Missing captions" },
        { value: "filled", text: "Has captions" },
      ],
    };
  },
  computed: {
    hasChanges() {
      return Object.keys(this.changedCaptions).length > 0;
    },
    fallbackLabel() {
      return this.job?.captionFallback === "filename"
        ? "the filename as a fallback"
        : "no fallback";
    },
    pageKicker() {
      return this.job?.status === "Draft"
        ? "Draft photo job"
        : "Caption manager";
    },
    canSchedulePending() {
      return this.job?.status === "Draft" || this.unscheduledPendingCount > 0;
    },
    scheduleButtonText() {
      if (this.starting) return "Scheduling...";
      return this.job?.status === "Draft"
        ? "Start Scheduling"
        : "Schedule Pending Posts";
    },
  },
  watch: {
    captionFilter() {
      this.currentPage = 1;
      this.fetchCaptionPosts();
    },
  },
  mounted() {
    if (this.$route.query.captionFilter) {
      this.captionFilter = this.$route.query.captionFilter;
    }
    this.fetchCaptionPosts();
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasChanges) {
      next();
      return;
    }

    if (window.confirm("You have unsaved caption changes. Leave anyway?")) {
      next();
    } else {
      next(false);
    }
  },
  methods: {
    async fetchCaptionPosts() {
      this.loading = true;
      try {
        const res = await axios.get(
          `/api/jobs/${this.$route.params.id}/caption-posts`,
          {
            params: {
              page: this.currentPage,
              pageSize: this.pageSize,
              search: this.search,
              captionFilter: this.captionFilter,
            },
          }
        );

        this.job = res.data.job;
        this.posts = res.data.data;
        this.totalPosts = res.data.totalPosts;
        this.missingCaptionCount = res.data.missingCaptionCount;
        this.filledCaptionCount = res.data.filledCaptionCount;
        this.unscheduledPendingCount = res.data.unscheduledPendingCount || 0;
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.error || "Failed to load captions"
        );
      } finally {
        this.loading = false;
      }
    },
    getCaptionValue(post) {
      return this.changedCaptions[post._id] ?? post.caption ?? "";
    },
    markCaptionChanged(post, caption) {
      this.$set(this.changedCaptions, post._id, caption);
    },
    openImagePreview(post) {
      if (!post?.thumbnailUrl) return;

      this.selectedImagePreview = {
        url: post.thumbnailUrl,
        fileName: post.fileName || "Photo preview",
      };
      this.showImagePreview = true;
    },
    goToJobDetail() {
      if (this.job?._id) {
        this.$router.push(`/automation-detail/${this.job._id}`);
      }
    },
    async saveChangedCaptions() {
      const captions = Object.entries(this.changedCaptions).map(
        ([postId, caption]) => ({ postId, caption })
      );

      if (!captions.length) return;

      this.saving = true;
      try {
        await axios.patch(`/api/jobs/${this.job.jobId}/captions`, {
          captions,
        });
        this.changedCaptions = {};
        this.$toast.success("Captions saved");
        await this.fetchCaptionPosts();
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.error || "Failed to save captions"
        );
      } finally {
        this.saving = false;
      }
    },
    async startScheduling() {
      if (this.hasChanges) {
        this.$toast.error("Save caption changes before scheduling");
        return;
      }

      if (
        this.missingCaptionCount > 0 &&
        this.job?.captionFallback === "none" &&
        !window.confirm(
          `${this.missingCaptionCount} posts have no caption. Start scheduling anyway?`
        )
      ) {
        return;
      }

      this.starting = true;
      try {
        await axios.post(`/api/jobs/${this.job.jobId}/start`);
        this.$toast.success("Scheduling started");
        this.$router.push(`/automation-detail/${this.job._id}`);
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.error || "Failed to start scheduling"
        );
      } finally {
        this.starting = false;
      }
    },
  },
};
</script>

<style scoped>
.caption-prep-page {
  display: grid;
  gap: 1rem;
  color: #162033;
}

.caption-hero,
.caption-toolbar,
.caption-stats,
.caption-table-card {
  background: #fff;
  border: 1px solid #e1e8f0;
  border-radius: 14px;
  box-shadow: 0 10px 24px rgba(16, 24, 40, 0.045);
}

.caption-hero {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.1rem;
}

.caption-kicker {
  color: #0b66d8;
  font-size: 0.82rem;
  font-weight: 800;
  text-transform: uppercase;
}

.caption-hero h1 {
  margin: 0.25rem 0;
  font-size: 1.6rem;
  font-weight: 850;
}

.caption-hero p {
  margin: 0;
  color: #64748b;
}

.hero-actions,
.caption-toolbar {
  display: flex;
  gap: 0.6rem;
  align-items: center;
  flex-wrap: wrap;
}

.caption-toolbar {
  padding: 0.8rem;
}

.caption-toolbar .form-control {
  min-width: 240px;
  flex: 1 1 260px;
}

.caption-toolbar .custom-select {
  max-width: 190px;
}

.caption-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  overflow: hidden;
}

.caption-stats > div {
  padding: 0.85rem 1rem;
  border-right: 1px solid #edf2f7;
}

.caption-stats > div:last-child {
  border-right: none;
}

.caption-stats span {
  display: block;
  color: #64748b;
  font-size: 0.82rem;
  font-weight: 750;
}

.caption-stats strong {
  display: block;
  margin-top: 0.2rem;
  color: #101828;
  font-size: 1.25rem;
}

.caption-table-card {
  padding: 0.9rem;
}

.caption-table >>> th {
  color: #334155;
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0;
  background: #f8fafc;
}

.file-name-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 180px;
  color: #334155;
  font-weight: 700;
}

.caption-thumbnail-button {
  position: relative;
  display: inline-flex;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: zoom-in;
}

.caption-thumbnail,
.caption-thumbnail-placeholder {
  width: 78px;
  height: 78px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

.caption-thumbnail {
  display: block;
  object-fit: cover;
  background: #f8fafc;
}

.caption-thumbnail-overlay {
  position: absolute;
  inset: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: #fff;
  background: rgba(15, 23, 42, 0.46);
  opacity: 0;
  transition: opacity 0.16s ease;
}

.caption-thumbnail-button:hover .caption-thumbnail-overlay,
.caption-thumbnail-button:focus-visible .caption-thumbnail-overlay {
  opacity: 1;
}

.caption-thumbnail-placeholder {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  background: #f8fafc;
  font-size: 0.64rem;
  font-weight: 700;
  text-align: center;
  line-height: 1.1;
  padding: 0.25rem;
}

.caption-preview-title {
  display: block;
  max-width: min(78vw, 980px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #0f172a;
  font-weight: 800;
}

::v-deep .caption-preview-header {
  border-bottom: 1px solid #e2e8f0;
}

::v-deep .caption-preview-body {
  padding: 0;
  background: #0f172a;
}

.caption-preview-image {
  display: block;
  width: 100%;
  max-height: 82vh;
  object-fit: contain;
  background: #0f172a;
}

.caption-status {
  display: inline-flex;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 750;
}

.caption-status.is-filled {
  color: #166534;
  background: #dcfce7;
}

.caption-status.is-missing {
  color: #92400e;
  background: #fef3c7;
}

@media (max-width: 767.98px) {
  .caption-hero {
    flex-direction: column;
  }

  .caption-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
